export const API_BASE = process.env.REACT_APP_API_BASE || '';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export function getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/** User id from the access token (source of truth for API auth). */
export function getAuthUserId() {
    const token = getAccessToken();
    if (!token) return null;
    try {
        const payloadPart = token.split('.')[1];
        if (!payloadPart) return null;
        const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
        const payload = JSON.parse(json);
        const raw =
            payload.sub ||
            payload.nameid ||
            payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
        const id = Number(raw);
        return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
        return null;
    }
}

export function setAuthTokens({ accessToken, refreshToken }) {
    if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearAuthTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function tryRefresh() {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;
    try {
        const res = await fetch(`${API_BASE}/Users/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        if (!data?.accessToken) return false;
        setAuthTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken || refreshToken });
        if (data.userProfileInfo) {
            const existing = JSON.parse(localStorage.getItem('user') || '{}');
            const next = {
                ...existing,
                ...data.userProfileInfo,
                isAdmin: Boolean(data.isAdmin),
            };
            localStorage.setItem('user', JSON.stringify(next));
        }
        return true;
    } catch {
        return false;
    }
}

/** Best-effort message from MSV API JSON or ASP.NET Problem responses. */
export function readApiError(result, fallback = 'Something went wrong. Please try again.') {
    if (!result || typeof result !== 'object') return fallback;

    if (result.errors && typeof result.errors === 'object') {
        for (const messages of Object.values(result.errors)) {
            if (Array.isArray(messages) && messages[0]) return String(messages[0]);
            if (typeof messages === 'string' && messages) return messages;
        }
    }

    return (
        result.errorMessage
        || result.ErrorMessage
        || result.detail
        || (result.title && result.title !== 'One or more validation errors occurred.' ? result.title : null)
        || fallback
    );
}

export async function parseApiResponse(response) {
    try {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
    } catch {
        return null;
    }
}

export async function apiFetch(path, { method, body, auth = true, headers } = {}) {
    const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
    const token = getAccessToken();
    const resolvedMethod = method || (body === undefined && path.includes('?') ? 'GET' : 'POST');
    const requestHeaders = {
        ...(resolvedMethod !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
        ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
    };

    const serializedBody = (body === undefined || resolvedMethod === 'GET')
        ? undefined
        : (typeof body === 'string' ? body : JSON.stringify(body));

    let response = await fetch(url, {
        method: resolvedMethod,
        headers: requestHeaders,
        body: serializedBody,
    });

    if (response.status === 401 && auth) {
        const refreshed = await tryRefresh();
        if (refreshed) {
            const retryToken = getAccessToken();
            response = await fetch(url, {
                method: resolvedMethod,
                headers: {
                    ...requestHeaders,
                    Authorization: `Bearer ${retryToken}`,
                },
                body: serializedBody,
            });
        }
    }

    return response;
}

export async function apiJson(path, options) {
    const response = await apiFetch(path, options);
    if (!response.ok) {
        const error = new Error(`Request failed: ${response.status}`);
        error.status = response.status;
        throw error;
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

const responseCache = new Map();
const DEFAULT_CACHE_TTL_MS = 45000;
const MAX_CACHE_ENTRIES = 80;

function cacheKey(path, options = {}) {
    const method = options.method
        || (options.body === undefined && path.includes('?') ? 'GET' : 'POST');
    let bodyPart = '';
    if (options.body !== undefined && method !== 'GET') {
        bodyPart = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
    }
    return `${method}:${path}:${bodyPart}`;
}

function pruneCache() {
    if (responseCache.size <= MAX_CACHE_ENTRIES) return;
    const oldest = responseCache.keys().next().value;
    responseCache.delete(oldest);
}

function cachedResponse(data, status = 200) {
    return {
        ok: status >= 200 && status < 300,
        status,
        json: async () => data,
        text: async () => (data == null ? '' : JSON.stringify(data)),
    };
}

/**
 * In-memory TTL cache for read-heavy API calls. Drop-in for apiFetch on successful JSON responses.
 */
export async function apiFetchCached(path, options = {}, { ttlMs = DEFAULT_CACHE_TTL_MS } = {}) {
    const key = cacheKey(path, options);
    const hit = responseCache.get(key);
    if (hit && hit.expires > Date.now()) {
        return cachedResponse(hit.data, hit.status);
    }

    const response = await apiFetch(path, options);
    if (!response.ok) {
        return response;
    }

    const text = await response.text();
    const data = text ? JSON.parse(text) : null;
    responseCache.set(key, {
        data,
        status: response.status,
        expires: Date.now() + ttlMs,
    });
    pruneCache();
    return cachedResponse(data, response.status);
}

export async function apiJsonCached(path, options = {}, cacheOptions) {
    const response = await apiFetchCached(path, options, cacheOptions);
    if (!response.ok) {
        const error = new Error(`Request failed: ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return response.json();
}

export function invalidateApiCache(pathPrefix) {
    if (!pathPrefix) {
        responseCache.clear();
        return;
    }
    for (const key of responseCache.keys()) {
        if (key.includes(pathPrefix)) responseCache.delete(key);
    }
}

